import { api } from "../..";
import { ApiConfig } from "../../ApiConfig";

const baseURL = ApiConfig.baseApi;

const getHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const buildUrl = (path = "") => `${baseURL}${ApiConfig.topup}${path}`;

// Contract-defined bounds for GET /topup/sales.
export const TOPUP_SALES_DEFAULT_LIMIT = 20;
export const TOPUP_SALES_MAX_LIMIT = 100;

// Phase 1 — SuperAdmin topup catalogue + country pricing.
// Phase 2 — SuperAdmin sales reporting (GET /topup/sales).
// Phase 3 — Customer reads (/available, /purchases, /usage).
// Phase 4 — Customer purchase (/checkout, /:purchaseId/confirm).
// /topup/payment/webhook is a server-to-server callback and must never be
// called from the frontend.
const topupService = {
  list: async () => {
    const url = buildUrl();
    return api.get(url, null, getHeaders());
  },

  getById: async (id) => {
    const url = buildUrl(`/${id}`);
    return api.get(url, null, getHeaders());
  },

  create: async (payload) => {
    const url = buildUrl();
    return api.post(url, payload, getHeaders());
  },

  // Entitlement fields only (name, packType/quantity range, availableToPlans,
  // isActive). Pricing changes go through the pricing endpoints below.
  update: async (id, payload) => {
    const url = buildUrl(`/${id}`);
    return api.put(url, payload, getHeaders());
  },

  activate: async (id) => {
    const url = buildUrl(`/${id}/activate`);
    return api.patch(url, {}, getHeaders());
  },

  deactivate: async (id) => {
    const url = buildUrl(`/${id}/deactivate`);
    return api.patch(url, {}, getHeaders());
  },

  // Soft delete. Returns 400 with "Cannot delete this topup pack as it has
  // already been purchased by customers." when any purchase references the
  // pack — surface that message and offer deactivate instead.
  delete: async (id) => {
    const url = buildUrl(`/${id}`);
    return api.delete(url, undefined, getHeaders());
  },

  getPricing: async (id) => {
    const url = buildUrl(`/${id}/pricing`);
    return api.get(url, null, getHeaders());
  },

  // Upsert one country's price — same country twice updates in place.
  upsertPricing: async (id, payload) => {
    const url = buildUrl(`/${id}/pricing`);
    return api.post(url, payload, getHeaders());
  },

  // Soft-disables pricing for that country; the pack stops being offered there.
  disablePricing: async (id, country) => {
    const url = buildUrl(`/${id}/pricing/${encodeURIComponent(country)}`);
    return api.delete(url, undefined, getHeaders());
  },

  // Customer — packs this company can buy right now. Already filtered
  // server-side by plan eligibility and by an active price existing for the
  // company's country: a pack with no price for that country is simply absent
  // from the array, never returned with a null/zero price.
  available: async () => {
    const url = buildUrl("/available");
    return api.get(url, null, getHeaders());
  },

  // Customer — this company's topup purchase history, newest first. The
  // contract returns a bare array; there is no pagination to consume.
  purchases: async () => {
    const url = buildUrl("/purchases");
    return api.get(url, null, getHeaders());
  },

  // Customer — combined allowance (plan + active topups) per numeric allowance
  // on the current plan. Returns the LIMIT side only, never current resource
  // counts; `combinedLimit` is authoritative and must never be recomputed as
  // planAllowance + activeTopupQuantity on the client.
  usage: async () => {
    const url = buildUrl("/usage");
    return api.get(url, null, getHeaders());
  },

  // Customer — start a purchase. `quantity` is only meaningful for a per_unit
  // pack; a fixed pack carries its own quantity server-side. Returns
  // { purchaseId, reference, authorizationUrl, amount, currency, quantity,
  // expiresAt } — amount/currency/expiresAt are authoritative and must never be
  // recomputed on the client.
  checkout: async (packId, quantity) => {
    const url = buildUrl("/checkout");
    const payload = { packId };
    if (quantity !== undefined && quantity !== null && quantity !== "") {
      payload.quantity = Number(quantity);
    }
    return api.post(url, payload, getHeaders());
  },

  // Customer — reconcile a purchase after the payment return. `:id` is the
  // purchaseId from the checkout response, never the packId and never the
  // reference. No request body: the identifier belongs in the URL.
  // Idempotent — the backend webhook activates the purchase independently, so
  // this may run after activation and simply returns the current state.
  confirm: async (purchaseId) => {
    const url = buildUrl(`/${purchaseId}/confirm`);
    return api.post(url, undefined, getHeaders());
  },

  // SuperAdmin sales/revenue view. All query params are optional; `limit`
  // defaults to 20 and is capped at 100 by the contract. startDate/endDate are
  // ISO dates filtering on purchasedAt. Response:
  // { purchases, pagination: { page, limit, total, pages }, totalRevenue }.
  // totalRevenue is computed server-side over active + expired purchases —
  // never recompute it from the returned rows.
  sales: async ({
    page,
    limit,
    packId,
    country,
    status,
    startDate,
    endDate,
  } = {}) => {
    const params = new URLSearchParams();

    if (page) params.append("page", page);

    const requestedLimit = Number(limit);
    const safeLimit = Number.isFinite(requestedLimit) && requestedLimit > 0
      ? Math.min(requestedLimit, TOPUP_SALES_MAX_LIMIT)
      : TOPUP_SALES_DEFAULT_LIMIT;
    params.append("limit", safeLimit);

    if (packId) params.append("packId", packId);
    if (country) params.append("country", country);
    if (status) params.append("status", status);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const queryString = params.toString();
    const url = `${buildUrl("/sales")}${queryString ? `?${queryString}` : ""}`;
    return api.get(url, null, getHeaders());
  },
};

export default topupService;
